import { BannerType } from "@/app/entities/Banner";
import { useBanners } from "@/app/hooks/useBanners";
import { useBannersMutations } from "@/app/hooks/useBannersMutations";
import { BannerCard } from "./BannerCard";
import { AddBannerButton } from "./AddBannerButton";
import { Spinner } from "@/view/components/ui/spinner";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import { useAuth } from "@/app/hooks/useAuth";

interface BannerSectionProps {
  type: BannerType;
  title: string;
}

export const BannerSection = ({ type, title }: BannerSectionProps) => {
  const { banners, isLoading } = useBanners(type);
  const { updateBanners } = useBannersMutations(type);

  const { isSupport, isSuperAdmin, isAdmin } = useAuth();

  const isDraggingDisabled = (index: number) => {
    return type === BannerType.DASHBOARD_CARD && index < 1;
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || !banners) return;

    if (
      type === BannerType.DASHBOARD_CARD &&
      (result.destination.index < 1 || result.source.index < 1)
    ) {
      return;
    }

    const reorderedBanners = Array.from(banners.banners);
    const [removed] = reorderedBanners.splice(result.source.index, 1);
    reorderedBanners.splice(result.destination.index, 0, removed);

    updateBanners(reorderedBanners);
  };

  if (isLoading) return <Spinner />;

  if (!banners) return <p>Nenhum banner disponível</p>;

  const canAddBanner = isSupport || isSuperAdmin || isAdmin;

  return (
    <div className="w-full space-y-4 overflow-x-auto rounded-md border border-gray-200 bg-gray-100 p-4">
      <h1 className="flex items-center gap-2 font-semibold">
        {title} <Spinner className="inline h-4 w-4" show={isLoading} />
      </h1>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId={`banner-list-${type}`} direction="horizontal">
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="flex w-full flex-row gap-4"
            >
              {banners.banners.map((banner, index) => (
                <>
                  <Draggable
                    key={banner.uri}
                    draggableId={banner.uri}
                    index={index}
                    isDragDisabled={isDraggingDisabled(index)}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={`banner-card ${
                          snapshot.isDragging ? "opacity-50" : ""
                        } ${
                          isDraggingDisabled(index)
                            ? "cursor-default opacity-90"
                            : ""
                        }`}
                      >
                        <BannerCard banner={banner} type={type} index={index} />
                      </div>
                    )}
                  </Draggable>
                  {type === BannerType.DASHBOARD_CARD && index === 0 && (
                    <div className="mx-2 h-[150px] border border-black/15" />
                  )}
                </>
              ))}
              {provided.placeholder}
              {canAddBanner &&
                [BannerType.LOGIN, BannerType.DASHBOARD_CARD].includes(
                  type,
                ) && <AddBannerButton type={type} />}

              {[
                BannerType.INITIAL_PAGE,
                BannerType.DASHBOARD_MODAL,
                BannerType.INVITE,
              ].includes(type) &&
                (!banners?.banners?.length || banners.banners.length === 0) && (
                  <AddBannerButton type={type} />
                )}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};
